import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../../zustand/loginStore";
import colorSelectorImage from "assets/colorSelector.png";
import {
  activateProduct,
  createAuthenticatedAxiosInstance,
  deactivateProduct,
  fetchCategories,
  fetchProductById,
  updateProduct,
} from "api/auth";
import {
  ColorVariant,
  Product,
  SizeCreate,
  UpdateProductRequest,
} from "interfaces/IProduct";
import CustomNavbar from "components/users/CustomNavbar";
import CustomBackButton from "components/general/CustomBackButton";
import CustomMultiSelect, {
  OptionType,
} from "components/general/CustomMultiSelect";
import ColorCard from "./ColorCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faQuestionCircle,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "components/general/LoadingScreen";
import logo from "assets/logo.png";
import "./ProductDetails.scss";
import CancelContinue from "components/general/CancelContinueModal";
import ConfirmationModal from "components/general/ConfirmationModal";
import { ActionMeta, MultiValue } from "react-select";
import { ChromePicker } from "react-color";
import { getColorName } from "utils/imagesAndColors";
import { useProductStore } from "../../../zustand/productStore";

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { sizes } = useProductStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [product, setProduct] = useState<Product | null>(null);
  const [colorVariants, setColorVariants] = useState<ColorVariant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [colorName, setColorName] = useState("");
  const [color, setColor] = useState("");
  const [colorVariantErrors, setColorVariantErrors] = useState<boolean[]>([]);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [categories, setCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [loadingStates, setLoadingStates] = useState({
    isProductFetched: false,
    isCategoriesFetched: false,
  });
  const [productChanges, setProductChanges] = useState<{
    deactivate_categories: number[];
    deleted_images: string[];
    deactivate_colors: string[];
    deactivate_size: { color: string; sizes: string[] }[];
  }>({
    deactivate_categories: [],
    deleted_images: [],
    deactivate_colors: [],
    deactivate_size: [],
  });
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  /**
   * Effect hook to fetch product details
   * @effect
   * @dependencies [id, token, product?.status]
   *
   * Fetches product data and initializes:
   * - Product details
   * - Color variants with their images and sizes
   * - Selected categories
   */
  useEffect(() => {
    const fetchProduct = async () => {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      try {
        const product: Product = await fetchProductById(
          axiosInstance,
          Number(id)
        );
        setProduct(product);
        setLoadingStates((prev) => ({ ...prev, isProductFetched: true }));

        // Transform product attributes into color variants
        const colorVariants: ColorVariant[] = product.attributes.map(
          (attr) => ({
            color: attr.color,
            colorName: attr.colorName,
            images: attr.images,
            sizes: attr.sizes.map((size) => ({
              size: size.size,
              price: parseFloat(size.price),
              stock: size.stock,
              description: size.description,
            })),
          })
        );

        setColorVariants(colorVariants);
        setSelectedCategories(
          product.categories.map((cat) => ({
            value: cat.category_id.toString(),
            label: cat.category,
          }))
        );
      } catch (error) {
        console.error("Error fetching product:", error);
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id, token, product?.status]);

  /**
   * Effect hook to load all available categories
   * @effect
   * @dependencies []
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchCategories(axiosInstance, {
          per_page: 1000,
        });
        const formattedCategories = response.categories.map((category) => ({
          value: category.id.toString(),
          label: category.name,
        }));
        setCategories(formattedCategories);
        setLoadingStates((prev) => ({ ...prev, isCategoriesFetched: true }));
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    loadCategories();
  }, []);

  /**
   * Effect hook to manage loading state
   * @effect
   * @dependencies [loadingStates]
   */
  useEffect(() => {
    if (loadingStates.isProductFetched && loadingStates.isCategoriesFetched) {
      setIsLoading(false);
    }
  }, [loadingStates]);

  /**
   * Toggles edit mode for the product
   */
  const handleEdit = () => {
    if (isEditing) {
      // Si estamos cancelando la edición, ocultar errores de validación
      setShowValidationErrors(false);
    }
    setIsEditing(!isEditing);
  };

  /**
   * Shows confirmation modal for status toggle
   */
  const handleToggleStatus = () => {
    setShowModal(true);
  };

  /**
   * Handles category selection changes
   * @param {MultiValue<OptionType>} newValue - New selected categories
   * @param {ActionMeta<OptionType>} actionMeta - Action metadata
   *
   * Tracks deactivated categories and updates state accordingly
   */
  const handleCategoryChange = (
    newValue: MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    const currentCategoryIds = newValue.map((cat) => parseInt(cat.value));
    const deactivatedCategories = product?.categories
      .filter((cat) => !currentCategoryIds.includes(cat.category_id))
      .map((cat) => cat.category_id);

    setSelectedCategories(newValue as { value: string; label: string }[]);
    setProductChanges((prev) => ({
      ...prev,
      deactivate_categories: deactivatedCategories || [],
    }));
  };

  /**
   * Handles image deletion tracking
   * @param {string} imageName - Name of the image to delete
   */
  const handleImageDelete = (imageName: string) => {
    setProductChanges((prev) => ({
      ...prev,
      deleted_images: [...prev.deleted_images, imageName],
    }));
  };

  /**
   * Handles color variant deletion
   * @param {number} index - Index of the color variant to delete
   *
   * Updates:
   * - Color variants array
   * - Deactivated colors list
   * - Deactivated sizes list
   */
  const handleDeleteColorVariant = (index: number) => {
    const colorToDeactivate = colorVariants[index].color;
    const sizesToDeactivate = colorVariants[index].sizes.map(
      (size) => size.size
    );

    const imagesToDelete = colorVariants[index].images
      .map((url) => url.split("/").pop())
      .filter((filename): filename is string => filename !== undefined);

    setColorVariants((prevVariants) =>
      prevVariants.filter((_, i) => i !== index)
    );

    setProductChanges((prev) => ({
      ...prev,
      deactivate_colors: [...prev.deactivate_colors, colorToDeactivate],
      deactivate_size: [
        ...prev.deactivate_size,
        {
          color: colorToDeactivate,
          sizes: sizesToDeactivate,
        },
      ],
      deleted_images: [...prev.deleted_images, ...imagesToDelete],
    }));
  };

  /**
   * Asynchronous function to handle confirming the status toggle
   * @async
   * @function handleConfirmToggle
   * @description Updates the product's status, updates the component state, and closes the modal
   */
  const handleConfirmToggle = async () => {
    if (!product) return;

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      let response;

      if (product.status === 1) {
        response = await deactivateProduct(axiosInstance, product.id);
      } else {
        response = await activateProduct(axiosInstance, product.id);
      }

      if (response.message) {
        setProduct({ ...product, status: product.status === 1 ? 0 : 1 });
        setShowModal(false);
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error toggling product status:", error);
      setShowErrorModal(true);
    }
  };
  /**
   * Handles color change in the color picker.
   * Updates the color state and fetches the corresponding color name.
   * @param {Object} newColor - The new color object from the color picker.
   * @param {string} newColor.hex - The hexadecimal value of the new color.
   */
  const handleColorChange = async (newColor: { hex: string }) => {
    setColor(newColor.hex);
    const colorName = await getColorName(newColor.hex);
    setColorName(colorName);
  };

  /**
   * Handles saving updates to a product
   * @async
   * @function handleSave
   *
   * The function:
   * 1. Validates product exists
   * 2. Validates all color descriptions are filled
   * 3. Filters out existing categories to avoid duplicates
   * 4. Constructs update data object with:
   *    - Basic product info (name, brand)
   *    - New categories only
   *    - Color variants with:
   *      - Only new base64 images (filters out existing URLs)
   *      - Size information (only sizes that have changed or have stock > 0 for new colors)
   *    - Additional changes (deactivations, deletions)
   * 5. Sends update request to API
   * 6. Handles success/error responses with modals
   */
  const handleSave = async () => {
    if (!product) return;
    
    // Validar que todas las descripciones estén completadas
    const emptyDescriptions = colorVariants.filter(variant => 
      !variant.sizes[0]?.description || variant.sizes[0]?.description.trim() === ''
    );
    
    if (emptyDescriptions.length > 0) {
      setShowValidationErrors(true);
      alert(`Por favor, completa la descripción para los siguientes colores:\n${emptyDescriptions.map(v => v.colorName).join('\n')}`);
      return;
    }
    
    // Ocultar errores de validación si todo está correcto
    setShowValidationErrors(false);
    setIsSaving(true);

    // Extract existing category IDs to filter duplicates
    const existingCategoryIds = product.categories.map(
      (cat) => cat.category_id
    );
    const newCategories = selectedCategories
      .map((cat) => parseInt(cat.value))
      .filter((catId) => !existingCategoryIds.includes(catId));

    // Get existing color variants from the original product
    const existingColors = product.attributes.map(attr => attr.color);

    // Construct update data object
    const updateData: UpdateProductRequest = {
      name: product.name,
      brand: product.brand,
      categories: newCategories,
      attributes: colorVariants.map((variant) => {
        // Check if this is a new color variant (not in the original product)
        const isNewColor = !existingColors.includes(variant.color);
        
        let filteredSizes;
        
        if (isNewColor) {
          // For new colors, only include sizes with stock > 0
          filteredSizes = variant.sizes.filter(size => size.stock > 0);
        } else {
          // For existing colors, find the original variant to compare
          const originalVariant = product.attributes.find(attr => attr.color === variant.color);
          
          if (originalVariant) {
            // Create a map of original sizes for quick lookup
            const originalSizesMap = new Map();
            originalVariant.sizes.forEach(size => {
              originalSizesMap.set(size.size, size.stock);
            });
            
            // Only include sizes that have changed or have stock > 0
            filteredSizes = variant.sizes.filter(size => {
              const originalStock = originalSizesMap.get(size.size) || 0;
              const currentStock = size.stock;
              
              // Include if:
              // 1. Stock has changed (from any value to any other value)
              // 2. Current stock > 0 (new stock being added)
              return originalStock !== currentStock || currentStock > 0;
            });
          } else {
            // Fallback: include all sizes if original variant not found
            filteredSizes = variant.sizes;
          }
        }

        return {
          color: variant.color,
          colorName: variant.colorName,
          images: variant.images.filter((img) => !img.startsWith("http")),
          sizes: filteredSizes,
        };
      }),
      ...productChanges,
    };

    try {
      const response = await updateProduct(
        axiosInstance,
        product.id,
        updateData
      );
      setIsSaving(false);

      if (response.product) {
        setShowSuccessModal(true);
        setIsEditing(false);
      } else {
        setShowErrorModal(true);
      }
    } catch (error: any) {
      setIsSaving(false);
      console.error("Error updating product:", error);
      
      // Manejar error 422 (validación del backend)
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        
        // Verificar si hay errores de descripción
        const descriptionErrors = Object.keys(errors).filter(key => 
          key.includes('description')
        );
        
        if (descriptionErrors.length > 0) {
          setShowValidationErrors(true);
          
          // Extraer información sobre qué colores necesitan descripción
          const colorIndices = descriptionErrors.map(key => {
            const match = key.match(/attributes\.(\d+)\.sizes\.\d+\.description/);
            return match ? parseInt(match[1]) : null;
          }).filter((index): index is number => index !== null);
          
          const uniqueColorIndices = Array.from(new Set(colorIndices));
          const colorNames = uniqueColorIndices.map(index => 
            colorVariants[index]?.colorName || `Color ${index + 1}`
          );
          
          if (colorNames.length > 0) {
            alert(`Por favor, completa la descripción para los siguientes colores:\n${colorNames.join('\n')}`);
          } else {
            alert("Por favor, completa todas las descripciones requeridas para cada color del producto.");
          }
          return;
        }
        
        // Si hay otros errores de validación, mostrar mensaje específico
        const errorMessages = Object.values(errors).flat().join('\n');
        alert(`Error de validación:\n${errorMessages}`);
        return;
      }
      
      // Manejar otros errores HTTP
      if (error.response?.data?.message) {
        alert(error.response.data.message);
        return;
      }
      
      // Para errores de red o desconocidos, mostrar el modal genérico
      setShowErrorModal(true);
    }
  };

  /**
   * Adds a new color variant to the product.
   * Initializes sizes for the new color variant if it doesn't already exist.
   * @param {Object} newColor - The new color object to add.
   * @param {string} newColor.color - The color value.
   * @param {string} newColor.name - The name of the color.
   */
  const handleAddColor = (newColor: { color: string; name: string }) => {
    if (!colorVariants.some((c) => c.color === color)) {
      const initializedSizes: SizeCreate[] = sizes
        .filter((size) => size.status !== 0)
        .map((size) => ({
          size: size.description, // Usar description en lugar de name
          price: 0,
          stock: 0,
          colorName: newColor.name,
          description: "", // Inicializar descripción vacía
        }));

      setColorVariants([
        ...colorVariants,
        {
          ...newColor,
          images: [],
          colorName: newColor.name,
          sizes: initializedSizes,
        },
      ]);
    } else {
      setShowColorModal(true);
    }
  };

  /**
   * Effect hook to handle clicks outside the color picker
   * @effect
   * @dependencies [showColorPicker]
   *
   * The effect:
   * 1. Creates event handler to detect clicks outside color picker
   * 2. Checks if click target is outside .color-picker-wrapper element
   * 3. Closes color picker if clicked outside
   * 4. Adds mousedown listener when color picker is shown
   * 5. Removes listener on cleanup to prevent memory leaks
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showColorPicker && !target.closest(".color-picker-wrapper")) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showColorPicker]);

  return (
    <>
      <CustomNavbar />
      <Container
        className="px-4 px-md-5 product-details"
        style={{ maxWidth: "1000px" }}
      >
        <Row className="mb-3">
          <CustomBackButton onClick={() => navigate("/inventories/products")} />

          <Col xs={12} className="text-center ">
            <img src={logo} alt="Logo" className="logo" />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            {isEditing ? (
              <h1>Editar Producto</h1>
            ) : (
              <h1>Detalles del Producto</h1>
            )}
          </Col>
        </Row>
        {isLoading && product ? (
          <LoadingScreen loadingText="Cargando información del producto..." />
        ) : (
          <>
            <Row className="mb-3">
              <Col xs={6}>
                <Form.Label className="fw-bold">No.</Form.Label>
                <div className="d-flex align-items-center">
                  <span style={{ fontSize: "24px", color: "#747775" }}>
                    {product?.id}
                  </span>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        ID único del producto
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                  </OverlayTrigger>
                </div>
              </Col>
              <Col xs={6} className="text-end">
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip className="custom-tooltip">
                      Estado actual del producto
                    </Tooltip>
                  }
                >
                  <span
                    className={`status-badge ${
                      product?.status ? "active" : "inactive"
                    }`}
                  >
                    {product?.status ? "Activo" : "Inactivo"}
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Nombre del producto</Form.Label>
                <Form.Control
                  type="text"
                  value={product?.name}
                  readOnly={!isEditing}
                  placeholder="Nombre del producto"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setProduct((prev) =>
                      prev ? { ...prev, name: e.target.value } : null
                    )
                  }
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Categoría</Form.Label>
                    <CustomMultiSelect
                      options={categories}
                      value={selectedCategories}
                      onChange={handleCategoryChange}
                      placeholder="Categorías seleccionadas"
                      isDisabled={!isEditing}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Marca</Form.Label>
                    <Form.Control
                      type="text"
                      value={product?.brand}
                      readOnly={!isEditing}
                      placeholder="Marca"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setProduct((prev) =>
                          prev ? { ...prev, brand: e.target.value } : null
                        )
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              {isEditing && (
                <Form.Group className="mb-3">
                  <Form.Label>
                    Color:
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          Selecciona el color en el circulo para poder agregarlo
                          a la lista de colores disponibles, dentro podras
                          colocar la cantidad de stock, tallas, descripción,
                          imagenes y el precio del producto
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        icon={faQuestionCircle}
                        className="ms-2"
                      />
                    </OverlayTrigger>
                  </Form.Label>
                  <Row className="align-items-center">
                    <Col md={6}>
                      <Form.Control
                        type="text"
                        value={colorName}
                        onChange={(e) => setColorName(e.target.value)}
                        placeholder="Nombre del color"
                      />
                    </Col>
                    <Col md={1} style={{ marginRight: "-1rem" }}>
                      {colorName === "" ? (
                        <Button
                          variant="link"
                          onClick={() => setShowColorPicker(true)}
                          style={{ padding: 0, border: "none" }}
                        >
                          <img
                            src={colorSelectorImage}
                            alt="Color Selector"
                            style={{ cursor: "pointer", height: "2rem" }}
                          />
                        </Button>
                      ) : (
                        <Button
                          variant="light"
                          onClick={() => setShowColorPicker(!showColorPicker)}
                          style={{
                            backgroundColor: "white",
                            border: "1px solid white",
                            padding: "0",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faCircle}
                            style={{
                              height: "2rem",
                              color: color,
                              border: "1px solid black",
                              borderRadius: "50%",
                              verticalAlign: "middle",
                            }}
                          />
                        </Button>
                      )}
                      {showColorPicker && (
                        <div
                          className="color-picker-wrapper"
                          style={{
                            position: "absolute",
                            zIndex: 2,
                            backgroundColor: "white",
                            borderRadius: "5px",
                          }}
                        >
                          <ChromePicker
                            color={color}
                            onChange={handleColorChange}
                            styles={{
                              default: {
                                picker: {
                                  backgroundColor: "rgba(251, 229, 132, 0.5)",
                                },
                              },
                            }}
                          />
                        </div>
                      )}
                    </Col>

                    <Col md={3}>
                      <Form.Control
                        type="text"
                        value={color}
                        readOnly
                        placeholder="#Hex"
                      />
                    </Col>
                    <Col md={2} style={{ width: "18.33333%" }}>
                      <Button
                        variant="primary"
                        className="w-100"
                        onClick={() =>
                          handleAddColor({ color: color, name: colorName })
                        }
                        disabled={!colorName || !color}
                      >
                        Añadir color
                      </Button>
                    </Col>
                  </Row>
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>
                  Colores y Variantes:
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Colores disponibles para este producto
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
                  </OverlayTrigger>
                </Form.Label>
              </Form.Group>
              {colorVariants.map(
                (colorVariant: ColorVariant, index: number) => (
                  <ColorCard
                    key={colorVariant.color}
                    colorVariant={colorVariant}
                    onUpdate={(updatedVariant: ColorVariant) => {
                      const newColorVariants: ColorVariant[] = [
                        ...colorVariants,
                      ];
                      newColorVariants[index] = updatedVariant;
                      setColorVariants(newColorVariants);
                    }}
                    onDelete={() => handleDeleteColorVariant(index)}
                    hasError={colorVariantErrors[index]}
                    isReadOnly={!isEditing}
                    onImageDelete={handleImageDelete}
                    showValidationErrors={showValidationErrors}
                  />
                )
              )}
            </Form>
            <Row className="mt-4 mb-5">
              <Col>
                <Button
                  className="bg-secundary"
                  variant="primary"
                  onClick={isEditing ? handleEdit : handleToggleStatus}
                >
                  {isEditing
                    ? "Cancelar"
                    : product?.status
                    ? "Desactivar producto"
                    : "Activar producto"}
                </Button>
              </Col>
              <Col className="text-end">
                <Button
                  variant="primary"
                  onClick={isEditing ? handleSave : handleEdit}
                >
                  {isEditing ? "Guardar Producto" : "Editar Producto"}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Container>
      {isSaving && <LoadingScreen loadingText="Guardando cambios..." />}
      <ConfirmationModal
        show={showColorModal}
        onHide={() => setShowColorModal(false)}
        title="Color ya añadido"
        message="Este color ya fue añadido anteriormente. Por favor, intenta con otro color."
        icon={faExclamationTriangle}
        iconColor="text-warning"
      />
      <CancelContinue
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={handleConfirmToggle}
        title={`${product?.status ? "Desactivar" : "Activar"} usuario`}
        message={`¿Estás seguro que deseas ${
          product?.status ? "desactivar" : "activar"
        } este usuario?`}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />{" "}
      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/inventories/products");
        }}
        title="Confirmación"
        message="Los cambios se han guardado con éxito"
        icon={faCheckCircle}
        iconColor="text-success"
      />
      <ConfirmationModal
        show={showErrorModal}
        onHide={() => {
          setShowErrorModal(false);
        }}
        title="Error"
        message={"Error al conectar con el servidor.\nInténtalo más tarde."}
        icon={faTimesCircle}
        iconColor="text-danger"
      />
    </>
  );
};

export default ProductDetails;
